import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icons";

/* Looking at a sheet without leaving the presentation.
 *
 * A board opened in a new tab takes the client out of the story and leaves them
 * to find their way back. So sheets open here: full-bleed, dark, arrow keys
 * between them, click to zoom in and drag to move around. Esc returns you to
 * exactly where you were. */

export interface Sheet { ref: string; name?: string; url: string }

export function SheetViewer({ sheets, start = 0, onClose }: { sheets: Sheet[]; start?: number; onClose: () => void }) {
  const [i, setI] = useState(start);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [idle, setIdle] = useState(false);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const sheet = sheets[i];

  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [i]);

  useEffect(() => {
    let t: number | undefined;
    const wake = () => { setIdle(false); window.clearTimeout(t); t = window.setTimeout(() => setIdle(true), 2600); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowRight") setI((x) => Math.min(sheets.length - 1, x + 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
      if (e.key === "0") { setZoom(1); setPan({ x: 0, y: 0 }); }
      wake();
    };
    wake();
    window.addEventListener("keydown", key);
    window.addEventListener("mousemove", wake);
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", key);
      window.removeEventListener("mousemove", wake);
      document.body.style.overflow = "";
    };
  }, [sheets.length, onClose]);

  if (!sheet) return null;

  const chrome = { opacity: idle && zoom === 1 ? 0 : 1, transition: "opacity .4s" } as const;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 230, background: "#0b0b0c", overflow: "hidden" }}>
      <div
        onWheel={(e) => {
          const next = Math.min(6, Math.max(1, zoom * (e.deltaY < 0 ? 1.18 : 1 / 1.18)));
          setZoom(next);
          if (next === 1) setPan({ x: 0, y: 0 });
        }}
        onPointerDown={(e) => {
          if (zoom === 1) return;
          drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) });
        }}
        onPointerUp={() => { drag.current = null; }}
        onDoubleClick={() => { setZoom((z) => (z === 1 ? 2.4 : 1)); setPan({ x: 0, y: 0 }); }}
        style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", cursor: zoom > 1 ? (drag.current ? "grabbing" : "grab") : "zoom-in", touchAction: "none" }}
      >
        <img
          src={sheet.url}
          alt={sheet.ref}
          draggable={false}
          onClick={() => zoom === 1 && setZoom(2.4)}
          style={{
            maxWidth: "100%", maxHeight: "100vh", objectFit: "contain", background: "#fff",
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: drag.current ? "none" : "transform .18s ease-out",
            userSelect: "none",
          }}
        />
      </div>

      {/* which sheet you're on */}
      <div style={{ position: "absolute", top: 16, left: 20, color: "#fff", ...chrome }}>
        <div className="mono" style={{ fontSize: 12.5 }}>{sheet.ref}</div>
        {sheet.name && <div style={{ fontSize: 12, opacity: .6, marginTop: 2 }}>{sheet.name}</div>}
      </div>

      <button onClick={onClose} aria-label="Close"
        style={{ position: "absolute", top: 14, right: 16, width: 38, height: 38, borderRadius: 19, display: "grid", placeItems: "center", cursor: "pointer", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)", color: "#fff", backdropFilter: "blur(6px)", ...chrome }}>
        <Icon.close width={16} />
      </button>

      {sheets.length > 1 && (
        <div className="row" style={{ position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)", gap: 10, alignItems: "center", ...chrome }}>
          <button className="iconbtn" disabled={i === 0} onClick={() => setI((x) => x - 1)}
            style={{ width: 34, height: 34, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)", color: "#fff" }}><Icon.back width={14} /></button>
          <span className="mono" style={{ color: "#fff", opacity: .7, fontSize: 12 }}>{i + 1} / {sheets.length}</span>
          <button className="iconbtn" disabled={i >= sheets.length - 1} onClick={() => setI((x) => x + 1)}
            style={{ width: 34, height: 34, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)", color: "#fff" }}><Icon.arrow width={14} /></button>
        </div>
      )}

      <div style={{ position: "absolute", bottom: 20, right: 20, color: "#fff", opacity: idle ? 0 : .45, fontSize: 11.5, transition: "opacity .4s" }}>
        {zoom > 1 ? "drag to move · double-click to fit" : "click to zoom"}{sheets.length > 1 ? " · ← → for the next sheet" : ""} · Esc to close
      </div>
    </div>, document.body);
}
